import { useMemo } from "react";
import Badge from "react-bootstrap/Badge";
import { resources } from "@constants/hardcoded-strings";
import { POLICY_COMPONENT_TYPE_OPTIONS } from "@constants/policy-component-type-options";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { PolicyDecisionConditionCategoryCell } from "@containers/common/policy-decision-condition-category-cell/policy-decision-condition-category-cell";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { PolicyDecisionConditionCategoryTypes } from "@enums/policy-decision-condition-category-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { SelectOption } from "@idw-types/common";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { PolicyComponentConfiguration } from "@idw-types/data/policy-component-configuration/policy-component-configuration";
import { toObject } from "@utils/object-util";
import { sortStrings } from "@utils/string-util";
import { PolicyComponentTableCell } from "../policy-component-table-cell/policy-component-table-cell";
import styles from "./policy-components-table-container.less";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";
import { ENTITY_STATUS_OPTIONS } from "@constants/entity-status-options";

type PolicyComponentsTableContainerProps = {
    policyComponents: Array<PolicyComponentConfiguration>;
    companies: Array<AxnCompany>;
    tableState: TableState;
    isPageHeight: boolean;
}

export type PolicyComponentsTableDataRow = PolicyComponentConfiguration & {
    organizationName: string;
    typeLabel: string;
};

export const PolicyComponentsTableContainer = (props: PolicyComponentsTableContainerProps) => {
    const {
        tableState,
        policyComponents,
        companies,
        isPageHeight
    } = props;
    const dispatch = useDispatch();

    const id = "policy-components";

    const policyComponentTypeList = useMemo(() => {
        return [...POLICY_COMPONENT_TYPE_OPTIONS, { label: "LIST", value: "LIST" }];
    }, [POLICY_COMPONENT_TYPE_OPTIONS]);

    const policyComponentTypeListMap = useMemo(() => {
        return toObject<SelectOption>(policyComponentTypeList, "value");
    }, [policyComponentTypeList]);

    const policyComponentsTableData: Array<PolicyComponentsTableDataRow> = useMemo(() => {
        return policyComponents.map(policyComponent => {
            const organization = companies.find(company => policyComponent.organizationID === company.id);
            return {
                ...policyComponent,
                organizationName: organization?.name || "IDDataweb (Public)",
                typeLabel: policyComponentTypeListMap[policyComponent.type].label,
                category: policyComponent?.category || resources.common.notFound
            }
        });

    }, [policyComponents, policyComponentTypeListMap, companies]);

    const categoryColumnFilterOptions = useMemo(() => {
        const options: Array<SelectOption> = [];

        if (policyComponentsTableData.some(tableRow => tableRow.category === PolicyDecisionConditionCategoryTypes.RISK)) {
            options.push({ label: "Risk", value: PolicyDecisionConditionCategoryTypes.RISK });
        }

        if (policyComponentsTableData.some(tableRow => tableRow.category === PolicyDecisionConditionCategoryTypes.CONTROL)) {
            options.push({ label: "Control", value: PolicyDecisionConditionCategoryTypes.CONTROL });
        }

        if (policyComponentsTableData.some(tableRow => tableRow.category === PolicyDecisionConditionCategoryTypes.VERIFICATION)) {
            options.push({ label: "Verification", value: PolicyDecisionConditionCategoryTypes.VERIFICATION });
        }

        return options;
    }, [policyComponentsTableData]);

    const columnHelper = createColumnHelper<PolicyComponentsTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("name", {
            header: resources.tables.deployedPolicyComponents.columns.name.header,
            filterFn: "fuzzyText",
            cell: (props) => <PolicyComponentTableCell policyComponentsTableDataRow={props.row.original} />,
            sortingFn: (a, b) => sortStrings(a.original.name, b.original.name)
        }),
        columnHelper.accessor("type", {
            header: resources.tables.deployedPolicyComponents.columns.type.header,
            filterFn: "singleSelectFilter",
            cell: (props) => <div>{props.row.original.typeLabel}</div>,
            meta: {
                filterOptions: policyComponentTypeList
            }
        }),
        columnHelper.accessor("category", {
            header: resources.tables.deployedPolicyComponents.columns.class.header,
            filterFn: "singleSelectFilter",
            cell: (props) => <PolicyDecisionConditionCategoryCell category={props.row.original.category} />,
            meta: {
                filterOptions: categoryColumnFilterOptions
            }
        }),
        columnHelper.accessor("organizationName", {
            header: resources.tables.deployedPolicyComponents.columns.organization.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.organizationName, b.original.organizationName)
        }),
        columnHelper.accessor("description", {
            header: resources.tables.deployedPolicyComponents.columns.description.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.description, b.original.description)
        }),
        columnHelper.accessor("status", {
            header: resources.tables.deployedPolicyComponents.columns.status.header,
            filterFn: "singleSelectFilter",
            meta: {
                filterOptions: ENTITY_STATUS_OPTIONS
            },
            cell: (props) => {
                switch (props.getValue()) {
                    case EntityStatusTypes.ACTIVE:
                        return <Badge bg={"success"}>{resources.common.active}</Badge>;
                    case EntityStatusTypes.INACTIVE:
                        return <Badge bg={"danger"}>{resources.common.inactive}</Badge>;
                    default:
                        return <span>{resources.common.notFound}</span>;
                }
            }
        })
    ]), [policyComponentsTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: policyComponentsTableData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.POLICY_COMPONENTS, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.POLICY_COMPONENTS, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.POLICY_COMPONENTS, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id={id}
                useTableResult={useTableResult}
                className={styles.policyComponentsTable}
                headerColumnKeyPrefix={id}
            />
            <PaginationContainer
                idPrefix={id}
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
}